import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { Scale, Users, MessageCircle, Heart, Shield, Target, Award, Sparkles, ArrowRight, Phone, Mail, MapPin, BookOpen, FileText, Video, Star, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Layout } from "@/components/Layout";
import { ServiceCard } from "@/components/ServiceCard";
import { ROUTE_PATHS, SERVICES_DATA } from "@/lib/index";
import { IMAGES } from "@/assets/images";

const iconMap = {
  Scale,
  Users,
  MessageCircle,
  Heart,
};

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { type: "spring", stiffness: 300, damping: 35 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const staggerItem = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { type: "spring", stiffness: 300, damping: 35 },
};

export default function Home() {
  const publics = [
    {
      titre: "Parents et familles",
      description: "Accompagnement bienveillant dans les moments difficiles de séparation et de coparentalité.",
      benefices: [
        "Écoute empathique et professionnelle",
        "Solutions adaptées à votre situation",
        "Soutien centré sur le bien-être de vos enfants",
      ],
      icon: Heart,
    },
    {
      titre: "Avocats et professionnels",
      description: "Expertise rigoureuse et rapports de qualité pour vos mandats judiciaires.",
      benefices: [
        "Méthodologie conforme aux standards",
        "Respect des délais et communication claire",
        "Crédibilité professionnelle reconnue",
      ],
      icon: Scale,
    },
    {
      titre: "Partenaires du réseau",
      description: "Collaboration professionnelle pour le bien-être des familles que vous accompagnez.",
      benefices: [
        "Services complémentaires spécialisés",
        "Approche collaborative et intégrée",
        "Références en toute confiance",
      ],
      icon: Users,
    },
  ];

  const valeurs = [
    {
      titre: "Intérêt de l'enfant",
      description: "Chaque décision, chaque recommandation est guidée par le bien-être et la sécurité de l'enfant.",
      icon: Heart,
    },
    {
      titre: "Neutralité",
      description: "Une approche objective et impartiale, respectueuse de toutes les parties impliquées.",
      icon: Shield,
    },
    {
      titre: "Rigueur",
      description: "Méthodologie structurée conforme aux plus hauts standards professionnels et éthiques.",
      icon: Target,
    },
    {
      titre: "Humanité",
      description: "Empathie, respect et bienveillance au cœur de chaque intervention auprès des familles.",
      icon: Sparkles,
    },
  ];

  return (
    <Layout>
      <SEO
        title="Expertise familiale en Outaouais — Gatineau"
        description="Centre d'expertise familiale de l'Outaouais : évaluation psychosociale, médiation familiale, coaching coparental à Gatineau. Spécialistes de la garde d'enfants et de la coparentalité."
        keywords="expertise familiale Gatineau, évaluation psychosociale Outaouais, médiation familiale Gatineau, coaching coparental, garde enfant Québec"
        canonical="/"
      />
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.BACKGROUND_CALM_1}
            alt=""
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.h1
              className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 text-foreground"
              variants={staggerItem}
            >
              Expertise familiale en Outaouais
            </motion.h1>
            <motion.p
              className="text-xl md:text-2xl text-muted-foreground mb-12 max-w-3xl mx-auto"
              variants={staggerItem}
            >
              Services psychosociaux spécialisés centrés sur l'intérêt de l'enfant, la rigueur professionnelle et l'accompagnement humain des familles.
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              variants={staggerItem}
            >
              <Button asChild size="lg" className="text-lg px-8 py-6">
                <Link to={ROUTE_PATHS.CONTACT}>
                  Prendre rendez-vous
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6">
                <Link to={ROUTE_PATHS.SERVICES}>Découvrir nos services</Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 300, damping: 35 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Nos services spécialisés</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Une gamme complète de services pour accompagner les familles dans les situations complexes de séparation et de coparentalité.
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {SERVICES_DATA.map((service, index) => (
              <ServiceCard key={service.id} service={service} index={index} />
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 300, damping: 35 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">À qui s'adressent nos services</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Une approche adaptée aux besoins spécifiques de chaque public, avec la même rigueur professionnelle.
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {publics.map((public_item, index) => {
              const IconComponent = public_item.icon;
              return (
                <motion.div key={index} variants={staggerItem}>
                  <Card className="h-full hover:shadow-lg transition-all duration-200">
                    <CardContent className="p-8">
                      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                        <IconComponent className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="text-2xl font-semibold mb-4">{public_item.titre}</h3>
                      <p className="text-muted-foreground mb-6">{public_item.description}</p>
                      <ul className="space-y-3">
                        {public_item.benefices.map((benefice, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                            <span className="text-sm">{benefice}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 300, damping: 35 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Nos valeurs fondamentales</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Les piliers qui guident chacune de nos interventions auprès des familles.
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {valeurs.map((valeur, index) => {
              const IconComponent = valeur.icon;
              return (
                <motion.div key={index} variants={staggerItem}>
                  <Card className="h-full text-center hover:shadow-lg transition-all duration-200">
                    <CardContent className="p-8">
                      <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-6">
                        <IconComponent className="h-8 w-8 text-secondary" />
                      </div>
                      <h3 className="text-xl font-semibold mb-4">{valeur.titre}</h3>
                      <p className="text-muted-foreground text-sm">{valeur.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 300, damping: 35 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Ressources et outils</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Accédez à nos ressources gratuites pour vous accompagner dans votre parcours.
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={staggerItem}>
              <Link to={ROUTE_PATHS.ADOPTION}>
                <Card className="h-full hover:shadow-lg transition-all duration-200 group">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-primary/20 transition-colors">
                      <Globe className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">Adoption internationale</h3>
                    <p className="text-muted-foreground text-sm">
                      Évaluation psychosociale pour l'adoption d'un enfant à l'étranger
                    </p>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>

            <motion.div variants={staggerItem}>
              <Link to={ROUTE_PATHS.BLOG}>
                <Card className="h-full hover:shadow-lg transition-all duration-200 group">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-secondary/20 transition-colors">
                      <BookOpen className="h-8 w-8 text-secondary" />
                    </div>
                    <h3 className="text-xl font-semibold mb-3 group-hover:text-secondary transition-colors">Blog & Ressources</h3>
                    <p className="text-muted-foreground text-sm">
                      Articles et conseils sur la coparentalité et la médiation
                    </p>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>

            <motion.div variants={staggerItem}>
              <Link to={ROUTE_PATHS.GUIDES}>
                <Card className="h-full hover:shadow-lg transition-all duration-200 group">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-accent/20 transition-colors">
                      <FileText className="h-8 w-8 text-accent-foreground" />
                    </div>
                    <h3 className="text-xl font-semibold mb-3 group-hover:text-accent-foreground transition-colors">Guides pratiques</h3>
                    <p className="text-muted-foreground text-sm">
                      Téléchargez nos guides pour vous préparer
                    </p>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>

            <motion.div variants={staggerItem}>
              <Link to={ROUTE_PATHS.VIDEOS}>
                <Card className="h-full hover:shadow-lg transition-all duration-200 group">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-primary/20 transition-colors">
                      <Video className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">Vidéos</h3>
                    <p className="text-muted-foreground text-sm">
                      Présentations et témoignages en vidéo
                    </p>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-accent/10">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 300, damping: 35 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Prêt à faire le premier pas?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Nous sommes là pour vous accompagner avec professionnalisme, empathie et rigueur. Contactez-nous pour discuter de votre situation en toute confidentialité.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button asChild size="lg" className="text-lg px-8 py-6">
                <Link to={ROUTE_PATHS.CONTACT}>
                  Nous contacter
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6">
                <Link to={ROUTE_PATHS.FAQ}>Questions fréquentes</Link>
              </Button>
            </div>
            <div className="grid md:grid-cols-3 gap-6 text-left">
              <Card>
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Par téléphone</h4>
                    <p className="text-sm text-muted-foreground">Réponse rapide à vos questions</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Par courriel</h4>
                    <p className="text-sm text-muted-foreground">Réponse sous 24-48h</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">En personne</h4>
                    <p className="text-sm text-muted-foreground">Bureau à Gatineau, Outaouais</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
